import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { RootState } from '../redux/store';
import { getOrderHistory } from '../services/authService';
import { BuyOrderHistorikk, BuyOrderStatus, BuylistPaymentMethod } from '../types';
import BuylistLayout from '../components/buylist/BuylistLayout';
import { theme, media } from '../styles/theme';
import { FoilBadge } from '../styles/components';

// ======== Helper functions ========

const getStatusLabel = (status: BuyOrderStatus): string => {
  switch (status) {
    case BuyOrderStatus.AAPNET: return 'Åpnet';
    case BuyOrderStatus.FERDIG: return 'Ferdig';
    case BuyOrderStatus.SENDT: return 'Sendt';
    case BuyOrderStatus.SENDT_MAIL: return 'E-post sendt';
    case BuyOrderStatus.BETALT: return 'Betalt';
    case BuyOrderStatus.STORE_CREDIT_BETALT: return 'Butikkreditt betalt';
    case BuyOrderStatus.KANSELLERT: return 'Kansellert';
    case BuyOrderStatus.FERDIG_GRADERT: return 'Ferdig gradert';
    default: return status;
  }
};

const getPaymentMethodLabel = (method: BuylistPaymentMethod): string => {
  switch (method) {
    case BuylistPaymentMethod.KONTO: return 'Bankkonto';
    case BuylistPaymentMethod.STORE_CREDIT: return 'Butikkreditt';
    default: return method;
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '–';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '–';
  return date.toLocaleDateString('nb-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// ======== Styled components ========

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${theme.colors.text};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: inherit;
`;

const ProfileCard = styled.div`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  box-shadow: ${theme.shadows.card};
  margin-bottom: 2rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const ProfileField = styled.div`
  margin-bottom: 0.5rem;
`;

const FieldLabel = styled.span`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
`;

const FieldValue = styled.span`
  color: ${theme.colors.white};
  font-size: 1rem;
`;

const StoreCreditBadge = styled.div`
  display: inline-block;
  background-color: rgba(45, 184, 107, 0.1);
  color: ${theme.colors.success};
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 1.1rem;
`;

const OrderCard = styled.div`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  box-shadow: ${theme.shadows.card};
  overflow: hidden;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-wrap: wrap;
  gap: 0.5rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const OrderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const OrderReference = styled.span`
  font-weight: 600;
  color: ${theme.colors.white};
`;

const OrderDate = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const OrderTotal = styled.span`
  font-weight: 600;
  color: ${theme.colors.success};
`;

const OrderCardCount = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const StatusBadge = styled.span<{ $status: BuyOrderStatus }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  color: ${theme.colors.white};
  background-color: ${({ $status }) => {
    switch ($status) {
      case BuyOrderStatus.BETALT:
      case BuyOrderStatus.STORE_CREDIT_BETALT:
        return theme.colors.success;
      case BuyOrderStatus.FERDIG:
      case BuyOrderStatus.FERDIG_GRADERT:
        return theme.colors.primary;
      case BuyOrderStatus.SENDT:
      case BuyOrderStatus.SENDT_MAIL:
        return theme.colors.accent;
      case BuyOrderStatus.KANSELLERT:
        return theme.colors.danger;
      case BuyOrderStatus.AAPNET:
      default:
        return theme.colors.gray.medium;
    }
  }};
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.2s;
  transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const OrderItems = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  background-color: rgba(0, 0, 0, 0.15);
`;

const ItemsTableWrapper = styled.div`
  overflow-x: auto;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ItemsTableHead = styled.thead`
  th {
    padding: 0.5rem;
    text-align: left;
    font-weight: 600;
    font-size: 0.85rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ItemsTableBody = styled.tbody`
  td {
    padding: 0.5rem;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: ${theme.colors.white};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${theme.colors.muted};
  font-size: 1.1rem;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${theme.colors.muted};
  font-size: 1.1rem;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.danger};
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.danger};
`;

const OrderTotalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// ======== Component ========

const ProfilePage: React.FC = () => {
  const { isAuthenticated, user, loading: authLoading } = useSelector(
    (state: RootState) => state.auth
  );

  const [orders, setOrders] = useState<BuyOrderHistorikk[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isAuthenticated) {
      setOrdersLoading(true);
      getOrderHistory()
        .then((data) => {
          setOrders(data);
          setOrdersError(null);
        })
        .catch(() => {
          setOrdersError('Kunne ikke hente ordrehistorikk. Prøv igjen senere.');
        })
        .finally(() => {
          setOrdersLoading(false);
        });
    }
  }, [isAuthenticated]);

  const toggleOrderExpanded = (ordreReferanse: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(ordreReferanse)) {
        next.delete(ordreReferanse);
      } else {
        next.add(ordreReferanse);
      }
      return next;
    });
  };

  if (!isAuthenticated && !authLoading) {
    return <Navigate to="/login" replace />;
  }

  return (
    <BuylistLayout>
      <Helmet>
        <title>Min profil | Tryllepapp</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageContainer>
        <PageTitle>Min profil</PageTitle>

        {user && (
          <ProfileCard>
            <SectionTitle>Kontoinformasjon</SectionTitle>
            <ProfileGrid>
              <ProfileField>
                <FieldLabel>Fornavn</FieldLabel>
                <FieldValue>{user.fornavn || '—'}</FieldValue>
              </ProfileField>
              <ProfileField>
                <FieldLabel>Etternavn</FieldLabel>
                <FieldValue>{user.etternavn || '—'}</FieldValue>
              </ProfileField>
              <ProfileField>
                <FieldLabel>E-post</FieldLabel>
                <FieldValue>{user.email || '—'}</FieldValue>
              </ProfileField>
              <ProfileField>
                <FieldLabel>Butikkreditt</FieldLabel>
                <StoreCreditBadge>
                  {user.storeCreditSaldo.toFixed(2)} kr
                </StoreCreditBadge>
              </ProfileField>
            </ProfileGrid>
            <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
              Navn og e-postadresse administreres via Shopify-kontoen din.
            </p>
          </ProfileCard>
        )}

        <SectionTitle>Ordrehistorikk</SectionTitle>

        {ordersLoading && (
          <LoadingState>Laster ordrehistorikk...</LoadingState>
        )}

        {ordersError && (
          <ErrorState>{ordersError}</ErrorState>
        )}

        {!ordersLoading && !ordersError && orders.length === 0 && (
          <EmptyState>Du har ingen tidligere ordrer.</EmptyState>
        )}

        {!ordersLoading && !ordersError && orders.map((order) => {
          const isExpanded = expandedOrders.has(order.ordreReferanse);
          return (
            <OrderCard key={order.ordreReferanse}>
              <OrderHeader onClick={() => toggleOrderExpanded(order.ordreReferanse)}>
                <OrderInfo>
                  <OrderReference>{order.ordreReferanse}</OrderReference>
                  <OrderDate>{formatDate(order.dato)}</OrderDate>
                  <StatusBadge $status={order.status}>
                    {getStatusLabel(order.status)}
                  </StatusBadge>
                  <OrderCardCount>{order.antallKort} kort</OrderCardCount>
                  <span>{getPaymentMethodLabel(order.betalingsmetode)}</span>
                </OrderInfo>
                <OrderTotalRow>
                  <OrderTotal>{order.totalsum.toFixed(2)} kr</OrderTotal>
                  <ExpandIcon $expanded={isExpanded}>&#9660;</ExpandIcon>
                </OrderTotalRow>
              </OrderHeader>

              {isExpanded && (
                <OrderItems>
                  <ItemsTableWrapper>
                  <ItemsTable>
                    <ItemsTableHead>
                      <tr>
                        <th>Kort</th>
                        <th>Versjon</th>
                        <th>Ekspansjon</th>
                        <th>Tilstand</th>
                        <th>Pris</th>
                      </tr>
                    </ItemsTableHead>
                    <ItemsTableBody>
                      {order.kort.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            {item.kortNavn}
                            {item.foil && <FoilBadge>Foil</FoilBadge>}
                          </td>
                          <td>{item.versjon}</td>
                          <td>{item.ekspansjon}</td>
                          <td>{item.tilstand}</td>
                          <td>{item.pris.toFixed(2)} kr</td>
                        </tr>
                      ))}
                    </ItemsTableBody>
                  </ItemsTable>
                  </ItemsTableWrapper>
                </OrderItems>
              )}
            </OrderCard>
          );
        })}
      </PageContainer>
    </BuylistLayout>
  );
};

export default ProfilePage;
